import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HolidayPayload, JobPositionPayload, ListHolidaysParams, ListParams, OrgUnitPayload,
  alterarSituacaoArea, alterarSituacaoCargo, alterarSituacaoFeriado, arvoreAreas,
  atualizarArea, atualizarCargo, atualizarFeriado, criarArea, criarCargo, criarFeriado,
  gerarFeriados, listarAreas, listarCargos, listarCentrosDeCusto, listarFeriados,
  LocationPayload, ListLocationsParams,
  alterarSituacaoLocalidade, atualizarLocalidade, criarLocalidade, listarLocalidades,
  listarClientesParaLocalidade, listarEmpresasParaLocalidade,
} from '../api';

export const CADASTROS_KEYS = {
  areas: ['cadastros', 'areas'] as const,
  areasLista: (p: ListParams) => ['cadastros', 'areas', 'lista', p] as const,
  areasArvore: ['cadastros', 'areas', 'arvore'] as const,
  centrosDeCusto: ['cadastros', 'centros-de-custo'] as const,
  cargos: ['cadastros', 'cargos'] as const,
  cargosLista: (p: ListParams) => ['cadastros', 'cargos', 'lista', p] as const,
  feriados: ['cadastros', 'feriados'] as const,
  feriadosLista: (p: ListHolidaysParams) => ['cadastros', 'feriados', 'lista', p] as const,
  localidades: ['cadastros', 'localidades'] as const,
  localidadesLista: (p: ListLocationsParams) => ['cadastros', 'localidades', 'lista', p] as const,
  clientesOpcoes: ['cadastros', 'localidades', 'clientes'] as const,
  empresasOpcoes: ['cadastros', 'localidades', 'empresas'] as const,
};

// --- Áreas ---------------------------------------------------------

export function useAreas(params: ListParams) {
  return useQuery({
    queryKey: CADASTROS_KEYS.areasLista(params),
    queryFn: () => listarAreas(params),
    placeholderData: keepPreviousData,
  });
}

export function useArvoreAreas() {
  return useQuery({ queryKey: CADASTROS_KEYS.areasArvore, queryFn: arvoreAreas });
}

export function useCentrosDeCusto(enabled: boolean) {
  return useQuery({
    queryKey: CADASTROS_KEYS.centrosDeCusto,
    queryFn: listarCentrosDeCusto,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useSalvarArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: OrgUnitPayload }) =>
      id ? atualizarArea(id, payload) : criarArea(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.areas }),
  });
}

export function useSituacaoArea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => alterarSituacaoArea(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.areas }),
  });
}

// --- Cargos --------------------------------------------------------

export function useCargos(params: ListParams) {
  return useQuery({
    queryKey: CADASTROS_KEYS.cargosLista(params),
    queryFn: () => listarCargos(params),
    placeholderData: keepPreviousData,
  });
}

export function useSalvarCargo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: JobPositionPayload }) =>
      id ? atualizarCargo(id, payload) : criarCargo(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.cargos }),
  });
}

export function useSituacaoCargo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => alterarSituacaoCargo(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.cargos }),
  });
}

// --- Feriados ------------------------------------------------------

export function useFeriados(params: ListHolidaysParams) {
  return useQuery({
    queryKey: CADASTROS_KEYS.feriadosLista(params),
    queryFn: () => listarFeriados(params),
    placeholderData: keepPreviousData,
  });
}

export function useSalvarFeriado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: HolidayPayload }) =>
      id ? atualizarFeriado(id, payload) : criarFeriado(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.feriados }),
  });
}

export function useSituacaoFeriado() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => alterarSituacaoFeriado(id, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.feriados }),
  });
}

export function useGerarFeriados() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (year: number) => gerarFeriados(year),
    onSuccess: () => qc.invalidateQueries({ queryKey: CADASTROS_KEYS.feriados }),
  });
}

// --- Localidades (issue #300) --------------------------------------

export function useLocalidades(params: ListLocationsParams) {
  return useQuery({
    queryKey: CADASTROS_KEYS.localidadesLista(params),
    queryFn: () => listarLocalidades(params),
    placeholderData: keepPreviousData,
  });
}

export function useSalvarLocalidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: LocationPayload }) =>
      id ? atualizarLocalidade(id, payload) : criarLocalidade(payload),
    onSuccess: () => {
      // Invalida também o cache usado pelos selects de ativo.
      void qc.invalidateQueries({ queryKey: CADASTROS_KEYS.localidades });
      void qc.invalidateQueries({ queryKey: ['master-data', 'locations'] });
    },
  });
}

export function useSituacaoLocalidade() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      alterarSituacaoLocalidade(id, active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CADASTROS_KEYS.localidades });
      void qc.invalidateQueries({ queryKey: ['master-data', 'locations'] });
    },
  });
}

/** Clientes ativos para o vínculo do tipo Cliente (RN05). */
export function useClientesParaLocalidade(enabled: boolean) {
  return useQuery({
    queryKey: CADASTROS_KEYS.clientesOpcoes,
    queryFn: listarClientesParaLocalidade,
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Empresas ativas para o vínculo de unidade da empresa (RN06). */
export function useEmpresasParaLocalidade(enabled: boolean) {
  return useQuery({
    queryKey: CADASTROS_KEYS.empresasOpcoes,
    queryFn: listarEmpresasParaLocalidade,
    enabled,
    staleTime: 5 * 60_000,
  });
}
